import { useMemo, useRef, useState, useEffect, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import { 
  OrbitControls, 
  Float, 
  Stage, 
  MeshReflectorMaterial, 
  Image as DreiImage,
  Text,
  PresentationControls
} from '@react-three/drei';
import * as THREE from 'three';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Product } from '../types';
import { useExperienceStore } from '../state/useExperienceStore';

const SafeImage = ({ url, fallbackUrl, boxScale = [1, 1], ...props }: any) => {
  const [validUrl, setValidUrl] = useState<string | null>(null);
  const [aspect, setAspect] = useState(1);

  useEffect(() => {
    let isMounted = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = () => {
      if (isMounted) {
        setAspect(img.width / img.height);
        setValidUrl(url);
      }
    };
    img.onerror = () => {
      if (isMounted) {
        console.warn(`SafeImage: Failed to load ${url}, using fallback.`);
        const fallback = fallbackUrl || `https://picsum.photos/seed/fallback/800/1200`;
        const fImg = new Image();
        fImg.crossOrigin = "anonymous";
        fImg.src = fallback;
        fImg.onload = () => {
          if (isMounted) {
            setAspect(fImg.width / fImg.height);
            setValidUrl(fallback);
          }
        };
        fImg.onerror = () => {
          if (isMounted) setValidUrl(fallback);
        };
      }
    };
    return () => { isMounted = false; };
  }, [url, fallbackUrl]);

  // Calculate fit scale within box
  const [boxW, boxH] = boxScale;
  const boxAspect = boxW / boxH;
  let finalScale: [number, number] = [boxW, boxH];
  
  if (aspect > boxAspect) {
    finalScale = [boxW, boxW / aspect];
  } else {
    finalScale = [boxH * aspect, boxH];
  }

  if (!validUrl) {
    return (
      <mesh {...props} scale={[...finalScale, 1]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial color="#111" transparent opacity={0.2} />
      </mesh>
    );
  }

  return (
    <Suspense fallback={null}>
      <DreiImage {...props} url={validUrl} scale={finalScale} />
    </Suspense>
  );
};

export const ProductSpotlight = () => {
  const { mode, selectedProductId, activeProductImageIndex } = useExperienceStore();
  const [product, setProduct] = useState<Product | null>(null);
  const meshRef = useRef<THREE.Mesh>(null!);

  useEffect(() => {
    if (selectedProductId) {
      const fetchProduct = async () => {
        const snap = await getDoc(doc(db, "products", selectedProductId));
        if (snap.exists()) setProduct({ id: snap.id, ...snap.data() } as Product);
      };
      fetchProduct();
    }
  }, [selectedProductId]);

  if (mode !== 'PRODUCT' || !selectedProductId) return null;

  return (
    <group position={[0, 0, 0]}>
      {/* Background Deep Black */}
      <mesh position={[0, 0, -20]}>
        <planeGeometry args={[100, 100]} />
        <meshBasicMaterial color="#000" />
      </mesh>

      <PresentationControls
        global
        rotation={[0, 0, 0]}
        polar={[-Math.PI / 4, Math.PI / 4]}
        azimuth={[-Math.PI / 2, Math.PI / 2]}
      >
        <group position={[0, 0, 0]}>
          <Float speed={1.5} rotationIntensity={0.1} floatIntensity={0.2}>
            <mesh ref={meshRef}>
              {/* Glass Background */}
              <planeGeometry args={[6.2, 9.2]} />
              <meshPhysicalMaterial 
                color="#ffffff"
                transparent
                opacity={0.06}
                roughness={0.05}
                thickness={1}
                transmission={0.5}
              />
              
              <SafeImage
                key={product?.images?.[activeProductImageIndex] || activeProductImageIndex}
                url={product?.images?.[activeProductImageIndex] || `https://picsum.photos/seed/${selectedProductId}-${activeProductImageIndex}/800/1200`}
                fallbackUrl={`https://picsum.photos/seed/${selectedProductId}-${activeProductImageIndex}/800/1200`}
                boxScale={[5.8, 8.8]}
                position={[0, 0, 0.1]}
                transparent
                toneMapped={false}
              />
              
              {/* Glowing Neon Border */}
              <mesh position={[0, 0, -0.05]}>
                <planeGeometry args={[6.3, 9.3]} />
                <meshBasicMaterial 
                  color="#ffffff" 
                  transparent 
                  opacity={0.2}
                  blending={THREE.AdditiveBlending}
                />
              </mesh>

              {/* Structural Neon Line Frame */}
              <lineSegments position={[0, 0, 0]}>
                <edgesGeometry args={[new THREE.PlaneGeometry(6.2, 9.2)]} />
                <lineBasicMaterial 
                  color="#ffffff" 
                  transparent 
                  opacity={0.6} 
                />
              </lineSegments>
            </mesh>
          </Float>
        </group>
      </PresentationControls>

      {/* Info Panel / Editorial Typography */}
      <group position={[4, 0, 1]}>
        <Text
          fontSize={0.25}
          color="white"
          anchorX="left"
          fontWeight="bold"
          fillOpacity={0.3}
          letterSpacing={0.2}
        >
          PRODUCT SPEC
        </Text>
        <Text
          position={[0, -0.6, 0]}
          fontSize={0.6}
          color="white"
          anchorX="left"
          fontWeight="medium"
          font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hjp-Ek-_EeA.woff"
        >
          {product?.name.toUpperCase() || "CARGANDO..."}
        </Text>
        <Text
          position={[0, -1.2, 0]}
          fontSize={0.3}
          color="white"
          anchorX="left"
          fontWeight="bold"
          fillOpacity={0.8}
        >
          ${product?.price?.toLocaleString()}
        </Text>
        
        <Text
          position={[0, -2.5, 0]}
          fontSize={0.2}
          color="white"
          maxWidth={4}
          anchorX="left"
          anchorY="top"
          lineHeight={1.6}
          fillOpacity={0.5}
        >
          {product?.description || "High-end selection from our exclusive partner store. Designed with precision and crafted for the modern individual."}
        </Text>

        <group position={[0, -4.5, 0]}>
          <mesh onClick={() => console.log('Buy')}>
            <planeGeometry args={[2.5, 0.8]} />
            <meshBasicMaterial color="white" />
          </mesh>
          <Text color="black" fontSize={0.2} position={[0, 0, 0.05]} fontWeight="bold">
            ADQUIRIR
          </Text>
        </group>
      </group>

      <spotLight 
        position={[10, 10, 10]} 
        intensity={2} 
        color="#ffffff" 
      />
    </group>
  );
};
